import type { SectionSpecItem } from "@/shared/models/SectionSpec";
import {
	type InspectionReadingPayload,
	statusOf,
	TOLERANCE_STATUS,
} from "../../models/Inspection";
import { REQUIRED_DIMENSION_MESSAGE } from "../../utils/inspectionFormValues";
import {
	getDraftReading,
	readingKey,
	toInspectionReading,
	toInspectionReadings,
	type InspectionFormDraft,
} from "./inspectionDraft";
import { validateMeasurement } from "@/shared/utils/measurementPrecision";
import {
	validateAngle,
	validateAngleValue,
} from "@/shared/utils/angleValidation";

const REQUIRED_HEAT_MESSAGE = "Heat number is required";
const validateInspectionHeat = (value: number | null): true | string =>
	value !== null || REQUIRED_HEAT_MESSAGE;

const numericReadingError = (value: number, code: string): string | null => {
	if (code === "LA1" || code === "LA2") return validateAngleValue(value);
	return validateMeasurement(String(value)).message;
};

export const hasValidDimensionReading = (
	draft: InspectionFormDraft,
	items: SectionSpecItem[],
): boolean => {
	const measured = items.filter(
		(item) => item.Applies && item.ValueTypeCode === "V",
	);
	if (
		measured.some((item) => {
			const angle = draft.angles[readingKey(item.ProfileItemId)];
			return angle && validateAngle(angle.degrees, angle.minutes).message;
		})
	)
		return false;
	return measured.some((item) => {
		try {
			const angle =
				item.Code === "LA1" || item.Code === "LA2"
					? (draft.angles[readingKey(item.ProfileItemId)] ?? {
							degrees: "",
							minutes: "",
						})
					: undefined;
			const row = toInspectionReading(getDraftReading(draft, item), angle);
			return (
				row.Checked === null &&
				row.NumericValue !== null &&
				numericReadingError(row.NumericValue, item.Code) === null
			);
		} catch {
			return false;
		}
	});
};

export const validateInspectionDate = (value: Date | null): true | string => {
	if (value === null) return "Sample At is required";
	return Number.isFinite(value.getTime()) || "Enter a valid sample date";
};

/** Shared mutation readings contain only entered values; weight is a separate header. */
const validateInspectionReadings = (
	readings: InspectionReadingPayload[],
	items: SectionSpecItem[],
): true | string => {
	if (
		new Set(readings.map((row) => row.ProfileItemId)).size !== readings.length
	)
		return "An item was sent more than once";
	const itemsById = new Map(items.map((item) => [item.ProfileItemId, item]));
	for (const row of readings) {
		const item = itemsById.get(row.ProfileItemId);
		if (!item?.Applies)
			return `Item not checked on this section: ${item?.Code ?? row.ProfileItemId}`;
		if (
			(item.ValueTypeCode === "V" &&
				(typeof row.NumericValue !== "number" || row.Checked !== undefined)) ||
			(item.ValueTypeCode === "A" &&
				(typeof row.Checked !== "boolean" || row.NumericValue !== undefined))
		) {
			return "Measured items take NumericValue and go / no go items take Checked";
		}
		if (row.NumericValue !== undefined) {
			const error = numericReadingError(row.NumericValue, item.Code);
			if (error) return `${item.Code}: ${error}`;
		}
	}
	if (
		!readings.some(
			(row) =>
				itemsById.get(row.ProfileItemId)?.ValueTypeCode === "V" &&
				row.NumericValue !== undefined,
		)
	)
		return REQUIRED_DIMENSION_MESSAGE;
	return true;
};

type DraftValidation =
	| {
			ok: true;
			heatId: number;
			readings: InspectionReadingPayload[];
			weight: number | null;
	  }
	| {
			ok: false;
			field: "heatId" | "readings" | "measuredLbsPerFoot";
			message: string;
	  };

/** Heat, readings and weight checks shared by the M1 entry page and sheet. */
export const validateDraftForSave = (
	draft: InspectionFormDraft,
	items: SectionSpecItem[],
	weightProfileItemId: number | null,
): DraftValidation => {
	const heat = validateInspectionHeat(draft.heatId);
	if (heat !== true || draft.heatId === null)
		return {
			ok: false,
			field: "heatId",
			message: typeof heat === "string" ? heat : REQUIRED_HEAT_MESSAGE,
		};
	let readings: InspectionReadingPayload[];
	try {
		readings = toInspectionReadings(draft, items);
	} catch {
		return {
			ok: false,
			field: "readings",
			message: "Complete the numeric measurements before saving",
		};
	}
	const readingValidation = validateInspectionReadings(readings, items);
	if (readingValidation !== true)
		return { ok: false, field: "readings", message: readingValidation };
	const weight = validateMeasurement(draft.measuredLbsPerFoot);
	if (weight.message || (weight.value !== null && weightProfileItemId === null))
		return {
			ok: false,
			field: "measuredLbsPerFoot",
			message: weight.message ?? "The WEIGHT item is missing for this mill",
		};
	return { ok: true, heatId: draft.heatId, readings, weight: weight.value };
};

type Limits = Pick<SectionSpecItem, "Ltl" | "Utl">;
type MeasurementFeedback = {
	error: boolean;
	helperText?: string;
	invalidParts?: ("Degrees" | "Minutes")[];
};

/** Tolerance warnings are visual feedback, never a reason to reject a save. */
const toleranceFeedback = (
	value: number | null,
	item: Limits,
): MeasurementFeedback => {
	const status = statusOf(value, item.Ltl, item.Utl);
	return {
		error:
			status === TOLERANCE_STATUS.OVER || status === TOLERANCE_STATUS.UNDER,
	};
};

export const measurementError = (
	raw: string,
	item: Limits,
): MeasurementFeedback => {
	const validation = validateMeasurement(raw);
	return validation.message
		? { error: true, helperText: validation.message }
		: toleranceFeedback(validation.value, item);
};

/** Whole degrees/minutes are distinct from scalar measurement text. */
export const angleMeasurementError = (
	degrees: string,
	minutes: string,
	item: Limits,
): MeasurementFeedback => {
	const validation = validateAngle(degrees, minutes);
	return validation.message
		? {
				error: true,
				helperText: validation.message,
				invalidParts: validation.invalidParts,
			}
		: toleranceFeedback(validation.value, item);
};
